/*
 * Boot, in the order the pieces depend on each other. The theme was already stamped on <html> by
 * the inline script in index.html, so the first paint is right; the rest happens here.
 *
 * 1. the module list becomes a registry, the store learns which module reduces which event, and
 *    what the modules offer core's own surfaces is installed (core/modules/bind.ts);
 * 2. the outbox is installed and the modules are bound to it, before anything renders — a surface
 *    that came up before its module was bound would keep the unbound calls, because binding is not
 *    a state change React re-renders on;
 * 3. the router is built and the session is asked for, in parallel — a module's routes and
 *    translations arrive with its bundle, so nothing renders before it: a shell whose links 404 is
 *    worse than a blank tab;
 * 4. the device is claimed for the account that is signing in, which forgets what another account
 *    left in this browser (core/session/device.ts) — before anything renders, because the home
 *    screen navigates to the remembered route as it mounts;
 * 5. the first render, and with it the client boots: open the database, hydrate, dial the socket,
 *    catch up (core/net/bootstrap.ts). Without a session the router goes to the login page.
 *
 * A module's full page (`FullPage`, core/modules/registry.ts) skips 2 and 4 and the client of 5: it
 * is drawn by the router alone, with or without a session, because it is where somebody arrives who
 * may not be let in — a visitor of an AI chat's preview — and it reads what it needs itself.
 *
 * A module's visitor page (`VisitorPage`) is where somebody without a session lands at an address
 * the module shares outside teamBase — a document's link address (DV W-1 §3.7). It is told apart only
 * once the session has answered nobody, so it skips 4 and the client of 5: no device claimed, no
 * database, no catch-up, a router of its own that draws nothing but the page, which dials its own
 * connection. Leaving it is a page load, as it is for a full page.
 *
 * StrictMode stays on: its double invocation in development is what surfaces an effect that is not
 * idempotent, and a chat client is made of those.
 */

import { createRootRoute, createRouter, type AnyRouter } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { App } from "./App.tsx"
import { installUnreadIndicator } from "./core/activity/index.ts"
import { authApi } from "./core/auth/index.ts"
import { bootComposer, preloadEditor } from "./core/composer/index.ts"
import { bindDocuments } from "./core/docs/index.ts"
import { bindModules, installProvisions } from "./core/modules/bind.ts"
import {
	fullPageAt,
	fullPagesOf,
	registerModules,
	visitorPageAt,
	type VisitorPage,
} from "./core/modules/registry.ts"
import { boot } from "./core/net/bootstrap.ts"
import type { Socket } from "./core/net/socket.ts"
import { installDesktopNotifications } from "./core/notify/index.ts"
import { openDb } from "./core/persist/idb.ts"
import { claimDevice } from "./core/session/device.ts"
import { bootOrSayWhy } from "./core/shell/bootFailure.ts"
import type { ConnectionState } from "./core/store/model.ts"
import { client } from "./core/store/store.ts"
import type { RequestData, RequestType } from "./core/wire/index.ts"
import { modules } from "./modules.ts"
import { createAppRouter } from "./routes/index.ts"
import { isSignedOutScreen } from "./routes/routes.tsx"
// The inert silhouette in index.html is painted from the same tokens as the real shell; its
// stylesheet has no component to import it, so the entry point carries it beside the floor.
import "./core/shell/frame.css"
import "./styles/base.css"

/**
 * `web/<sha>`, the build this client names in its hello (protocol.md §6). The server keeps it on the
 * connection for the record and compares nothing against it: what decides whether the two can talk
 * is the protocol version.
 */
const BUILD = "web/dev"

const root = document.getElementById("root")
if (!root) throw new Error("index.html has no #root")
const container = root

/*
 * The scroll lock behind every Radix overlay injects a <style> element, and `style-src 'self'`
 * refuses one that carries no nonce: the page behind an open dialog would keep scrolling, and the
 * console would carry a policy violation per dialog. The library reads the value from this global
 * (get-nonce), and the document's own nonce is the one the server minted for this response, so the
 * injected stylesheet is allowed by exactly the policy that names it.
 */
declare global {
	var __webpack_nonce__: string | undefined
}
globalThis.__webpack_nonce__ =
	document.querySelector<HTMLScriptElement>("script[nonce]")?.nonce ?? undefined

const registry = registerModules(modules)
// Wrapped rather than passed: the registry's lookup is a method of the object it belongs to.
client.setReducerLookup((type) => registry.reducerFor(type))
installProvisions(registry)

// The module's full page this document was opened at, if it was: its chunk is asked for now, while
// the bundle is still being evaluated, for the reason routes.tsx starts a sign-in screen's early.
const fullPages = fullPagesOf(registry)
const fullPage = fullPageAt(fullPages, window.location.pathname)
void fullPage?.load()

// The socket exists only once the session is known, and the outbox has to exist before the first
// render. It therefore sends through this holder: a request made before the socket is up is
// rejected exactly as one made while it is down, which is the case the outbox already handles by
// keeping the item and retrying it.
let socket: Socket | undefined
const request = <T extends RequestType>(type: T, data: RequestData<T>) =>
	socket === undefined
		? Promise.reject(new Error("teambase: the socket is not connected"))
		: socket.send(type, data)

/**
 * The client's half that needs no session: the outbox, the modules bound to it and the editor's
 * chunk. It runs before anything renders — a surface that came up before its module was bound would
 * keep the unbound calls, because binding is not a state change React re-renders on — and it runs
 * nowhere a full page is drawn, which writes nothing, sends nothing and has no composer: a
 * preview's visitor on a phone opens no drafts database and downloads no editor.
 */
function bootClientHalf(): void {
	const outbox = bootComposer()
	// Bound for the page's life: signing out reloads the page, so nothing here ever unbinds.
	bindModules(registry, { client, outbox, request })
	// The editor's chunk, fetched while the session is still being asked for: on every visit but the
	// very first it is cached before a conversation mounts (F-2 §6.1).
	preloadEditor()

	// The outbox is the one home of what is on its way out; the model carries a copy so that a
	// screen renders the pending rows without subscribing to a second store (spec 12 §3.4).
	outbox.subscribe((items) => client.api.setState({ outbox: [...items] }))

	// Dispatching resumes only after a catch-up has been applied: the socket reports "open" after its
	// catchUp returned (protocol.md §2.10) and the model's connection field is that status, so
	// following it here is following the rule. It is followed from the first moment, not from the
	// first change: a tab that is still booting has no socket, and an outbox that is not paused
	// competes for the lead — which it would then hold in front of a tab that could send
	// (protocol.md §2.12).
	let dispatching: boolean | undefined
	function followConnection(connection: ConnectionState): void {
		const open = connection === "open"
		if (open === dispatching) return
		dispatching = open
		if (open) outbox.resume()
		else outbox.pause()
	}
	followConnection(client.api.getState().connection)
	client.api.subscribe((model) => followConnection(model.connection))
}
// An address a module shares outside teamBase may be a visitor's (`VisitorPage`), who has no outbox
// and no drafts: there the half waits for the session to name somebody.
const visitorAddress = visitorPageAt(registry, window.location.pathname)
if (fullPage === undefined && visitorAddress === undefined) bootClientHalf()

function render(router: AnyRouter): void {
	createRoot(container).render(
		<StrictMode>
			<App services={{ store: client.api, registry }} router={router} />
		</StrictMode>,
	)
}

/**
 * A full page is drawn by the router alone: no session asked for, no device claimed, no outbox, no
 * database, no socket. What it needs it reads itself, and it leaves by a page load (`FullPage`). A router
 * navigation away from it — a link a page forgot to make a plain one — becomes that page load
 * here, before anything renders the shell around a client that never booted.
 */
async function startFullPage(): Promise<void> {
	const router = await createAppRouter(registry)
	router.subscribe("onBeforeLoad", ({ toLocation }) => {
		if (fullPageAt(fullPages, toLocation.pathname) === undefined)
			window.location.assign(toLocation.href)
	})
	render(router)
}

/**
 * A visitor's page, under a router whose one route draws it whatever the address: the page reads
 * its address itself, and a link out of it is a page load.
 */
async function startVisitorPage(page: VisitorPage): Promise<void> {
	const Screen = await page.load()
	const search = new URLSearchParams(window.location.search)
	const routeTree = createRootRoute({
		component: () => <Screen pathname={window.location.pathname} search={search} build={BUILD} />,
	})
	render(createRouter({ routeTree }))
}

async function start(): Promise<void> {
	if (fullPage !== undefined) return startFullPage()

	// The session and the module bundles are asked for together: neither waits on the other, and
	// the two round trips are one.
	const [session, router] = await Promise.all([authApi.session(), createAppRouter(registry)])
	const user = session.user

	// Whose device is this? What the browser remembers belongs to one account, and a browser that
	// was somebody else's forgets their drafts, their outbox and where they were — before the first
	// render, because the home screen navigates to the remembered route as it mounts, and before
	// the socket, because the outbox dispatches the moment it is open (core/session/device.ts).
	if (user) await claimDevice(user.id)

	// Nobody signed in at an address a module shares outside teamBase: that module's visitor page.
	if (visitorAddress !== undefined) {
		if (!user) return startVisitorPage(visitorAddress)
		bootClientHalf()
	}

	render(router)

	if (!user) {
		// The signed-out screens are where a visitor without a session belongs; anywhere else is
		// the login page.
		if (!isSignedOutScreen(window.location.pathname)) {
			await router.navigate({ to: "/login" })
		}
		return
	}

	// Every module's object stores are declared into the one upgrade function; a browser that
	// refuses site data answers `undefined`, and the client runs from memory with a banner.
	const db = await openDb(registry.all().flatMap((mod) => mod.idbStores ?? []))
	const runtime = await boot({
		client,
		registry,
		session: { userId: user.id, csrf: session.csrfToken, locale: user.locale },
		db,
		build: BUILD,
		onRevoked: () => void router.navigate({ to: "/login" }),
	})
	socket = runtime.socket

	// The documents send through the same socket. They are bound here, once the reader is known,
	// because the flusher that brings a closed tab's unsaved steps to the server reads its rows by
	// the person they belong to.
	bindDocuments({
		user: () => user.id,
		request,
		// The state as it stands first: a view that subscribes after the socket opened would hear of
		// no change and stay offline until the next one.
		connection: (on) => {
			on(client.api.getState().connection)
			return client.api.subscribe((m) => on(m.connection))
		},
	})

	// The tab's own report of what is waiting: title, favicon dot and platform badge. It lives at
	// the boot and not on the Activity page, because the number has to be right in a tab that is
	// showing a conversation, or nothing at all.
	installUnreadIndicator(client.api)

	// The same hint, on the desktop: a banner while the window is not the one being looked at. The
	// listener is installed for the session's lifetime and decides nothing on its own — the server
	// says who is to be told, the account's preferences say whether to show it, and the browser's
	// own permission has the last word (core/notify/desktop.ts).
	installDesktopNotifications(client, { registry })
}

// A boot that does not arrive leaves the silhouette of `frame.css` standing with nothing behind
// it — a grey window that never becomes anything, which is what a failed session request, a failed
// chunk and a request that is never answered at all look like. This paints the one line there is
// to say, on a rejection and on a deadline alike (`core/shell/bootFailure.ts`); after the first
// render it leaves the window alone, because by then the application is up and says what is wrong
// in its own surfaces.
bootOrSayWhy(start)
